import clsx from "clsx";
import { Link, navigate } from "gatsby";
import { useSetAtom } from "jotai";
import type { MouseEvent } from "react";
import { useState } from "react";

import { DownloadImg, GiftImg } from "@/assets/header";
import { LogoV2Img } from "@/assets/logo";
import { isRegistrationModalOpenAtom } from "@/context/registration";
import { WHITE_PAPER_URL } from "@/data/links";
import { scrollToSection } from "@/hooks/useSmoothScroll";

const isSportsPath = (pathname: string) => pathname === "/sports";
const isExchangePath = (pathname: string) =>
  pathname === "/" || pathname === "/exchange";

// Earn/Contract live as sections on the Exchange page. Same-page navigation
// doesn't remount the homepage, so scroll directly when already there.
const handleSectionClick = (event: MouseEvent, section: string) => {
  if (isExchangePath(window.location.pathname)) {
    event.preventDefault();
    scrollToSection(section);
  }
};

// The download button scrolls to the app-download section when the current
// page has one, otherwise falls back to the homepage's download section.
const handleDownloadClick = () => {
  if (document.getElementById("download")) {
    scrollToSection("download");
  } else {
    navigate("/?section=download");
  }
};

function PillLink({
  to,
  isActive,
  children,
}: {
  to: string;
  isActive: (pathname: string) => boolean;
  children: string;
}) {
  return (
    <Link
      to={to}
      getProps={({ location }) => ({
        className: clsx(
          "flex items-center justify-center",
          "w-[90px] h-[28px] rounded-[100px]",
          "text-[14px] leading-[20px] text-center whitespace-nowrap",
          "transition-all duration-300",
          isActive(location.pathname)
            ? "bg-pill-active text-t-primary font-bold"
            : "text-t-tertiary font-medium",
        ),
      })}
    >
      {children}
    </Link>
  );
}

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const setShowRegistrationModal = useSetAtom(isRegistrationModalOpenAtom);

  const navLinks = (
    <>
      <Link
        to="/?section=earn"
        onClick={(event) => {
          setIsMobileMenuOpen(false);
          handleSectionClick(event, "earn");
        }}
        className={clsx(
          "text-[15px] leading-[22px] text-white whitespace-nowrap",
          "hover:text-brand transition-colors",
        )}
      >
        Earn
      </Link>
      <Link
        to="/?section=contracts"
        onClick={(event) => {
          setIsMobileMenuOpen(false);
          handleSectionClick(event, "contracts");
        }}
        className={clsx(
          "text-[15px] leading-[22px] text-white whitespace-nowrap",
          "hover:text-brand transition-colors",
        )}
      >
        Contract
      </Link>
      <Link
        to="/invite-friends"
        onClick={() => setIsMobileMenuOpen(false)}
        className={clsx(
          "flex items-center gap-[10px] whitespace-nowrap",
          "text-[15px] leading-[22px] font-bold text-brand",
          "hover:opacity-85 transition-opacity",
        )}
      >
        <img src={GiftImg} alt="" className="size-[24px]" />
        Invite Friends
      </Link>
      <a
        href={WHITE_PAPER_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => setIsMobileMenuOpen(false)}
        className={clsx(
          "text-[15px] leading-[22px] text-white whitespace-nowrap",
          "hover:text-brand transition-colors",
        )}
      >
        White Paper
      </a>
    </>
  );

  return (
    <header
      className={clsx(
        "absolute top-0 left-0 z-50 w-full h-[64px]",
        "flex items-center justify-between",
        "px-4 md:px-6 xl:px-[40px]",
      )}
    >
      {/* Left: logo + pill + nav */}
      <div className="flex items-center gap-4 xl:gap-[44px]">
        {/* Mobile hamburger */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="lg:hidden p-0 hover:opacity-85 active:opacity-75 transition-opacity"
          aria-label="Toggle menu"
        >
          <svg
            className="w-5 h-5 text-white"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            {isMobileMenuOpen ? (
              <path d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>

        <Link to="/" className="hidden lg:block shrink-0">
          <img src={LogoV2Img} alt="Tuuuz" className="w-[127px] h-[32px]" />
        </Link>

        <div
          className={clsx(
            "flex items-center p-[2px]",
            "bg-pill-bg rounded-[100px]",
          )}
        >
          <PillLink to="/" isActive={isExchangePath}>
            Exchange
          </PillLink>
          <PillLink to="/sports" isActive={isSportsPath}>
            Sports
          </PillLink>
        </div>

        <nav className="hidden lg:flex items-center gap-4 xl:gap-[44px]">
          {navLinks}
        </nav>
      </div>

      {/* Right: register + icons */}
      <div className="flex items-center gap-4 xl:gap-[40px] h-[40px]">
        <button
          onClick={() => setShowRegistrationModal(true)}
          className={clsx(
            "bg-brand text-black",
            "px-[14px] py-[8px] rounded-[97px]",
            "text-[15px] leading-[22px] font-medium whitespace-nowrap",
            "hover:opacity-85 active:opacity-75 transition-opacity",
          )}
        >
          Register
        </button>
        <button
          type="button"
          onClick={handleDownloadClick}
          className="hidden md:block hover:opacity-85 active:opacity-75 transition-opacity"
          aria-label="Download"
        >
          <img src={DownloadImg} alt="" className="size-[22px]" />
        </button>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div
          className={clsx(
            "lg:hidden",
            "absolute top-[64px] left-0 right-0 z-50",
            "bg-page-black border-t border-white/10",
          )}
        >
          <nav className="flex flex-col items-start gap-4 p-4">{navLinks}</nav>
        </div>
      )}
    </header>
  );
}
